/* eslint-disable jsx-a11y/label-has-associated-control */
import Loader from 'components/atoms/loader';
import { BiddingDataTypes } from 'interfaces/robot_dashboard';
import { Line } from 'rc-progress';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { fetchRobotDashboardBiddings } from 'services/actions/robot_dashboard_actions';
import DataTable from 'react-data-table-component';
import { datatableColumns, datatableData } from './datatable_list';

const STATUS_OPTIONS = ['Cadastrada', 'Configurada', 'Encerrada', 'Em Disputa'];

const formatPortalLabel = (portal: string) =>
  portal.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RobotDashboardBiddingsList = () => {
  const [biddingsList, setBiddingsList] = useState<Array<BiddingDataTypes> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const saved = localStorage.getItem('robot_biddings_rows_per_page');
    return saved ? Number(saved) : 25;
  });

  const [filterClientId, setFilterClientId] = useState('');
  const [filterPrgCod, setFilterPrgCod] = useState('');
  const [filterUasg, setFilterUasg] = useState('');
  const [filterOrgName, setFilterOrgName] = useState('');
  const [filterPortal, setFilterPortal] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [tooltip, setTooltip] = useState<{ text: string; top: number; left: number } | null>(null);

  const loadBiddings = () => {
    setError(false);
    setDownloadProgress(0);
    fetchRobotDashboardBiddings(
      setBiddingsList,
      setLoading,
      () => {},
      setError,
      setDownloadProgress,
    );
  };

  useEffect(() => {
    loadBiddings();
  }, []);

  const showTooltip = (e: React.MouseEvent<HTMLButtonElement>, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, top: rect.top + rect.height / 2, left: rect.right + 8 });
  };

  const hideTooltip = () => setTooltip(null);

  const portalOptions = useMemo(() => {
    if (!biddingsList) return [];
    return Array.from(
      new Set(biddingsList.map((b) => b.portal).filter(Boolean)),
    ).sort() as string[];
  }, [biddingsList]);

  const filteredList = useMemo(() => {
    if (!biddingsList) return null;
    return biddingsList.filter((b: BiddingDataTypes) => {
      if (filterClientId && !String(b.clientId).includes(filterClientId)) return false;
      if (filterPrgCod && !(b.prgCod || '').toLowerCase().includes(filterPrgCod.toLowerCase()))
        return false;
      if (filterUasg && !(b.uasg || '').toLowerCase().includes(filterUasg.toLowerCase()))
        return false;
      if (filterOrgName && !(b.orgName || '').toLowerCase().includes(filterOrgName.toLowerCase()))
        return false;
      if (filterPortal && b.portal !== filterPortal) return false;
      if (filterStatus && b.status !== filterStatus) return false;
      if (filterDateStart) {
        if (!b.disputeStartDate) return false;
        if (b.disputeStartDate.slice(0, 10) < filterDateStart) return false;
      }
      if (filterDateEnd) {
        if (!b.disputeStartDate) return false;
        if (b.disputeStartDate.slice(0, 10) > filterDateEnd) return false;
      }
      return true;
    });
  }, [
    biddingsList,
    filterClientId,
    filterPrgCod,
    filterUasg,
    filterOrgName,
    filterPortal,
    filterStatus,
    filterDateStart,
    filterDateEnd,
  ]);

  const hasActiveFilters =
    filterClientId ||
    filterPrgCod ||
    filterUasg ||
    filterOrgName ||
    filterPortal ||
    filterStatus ||
    filterDateStart ||
    filterDateEnd;

  const clearFilters = () => {
    setFilterClientId('');
    setFilterPrgCod('');
    setFilterUasg('');
    setFilterOrgName('');
    setFilterPortal('');
    setFilterStatus('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  const applyQuickFilter = (status: string, dateStart: string, dateEnd: string) => {
    setFilterClientId('');
    setFilterPrgCod('');
    setFilterUasg('');
    setFilterOrgName('');
    setFilterPortal('');
    setFilterStatus(status);
    setFilterDateStart(dateStart);
    setFilterDateEnd(dateEnd);
  };

  const quickFilters = [
    {
      label: 'Hoje em disputa',
      description: 'Pregões com abertura de lances hoje e status Em Disputa',
      status: 'Em Disputa',
      dateStart: moment().format('YYYY-MM-DD'),
      dateEnd: moment().format('YYYY-MM-DD'),
    },
    {
      label: 'Hoje configurados',
      description: 'Pregões configurados com abertura de lances hoje',
      status: 'Configurada',
      dateStart: moment().format('YYYY-MM-DD'),
      dateEnd: moment().format('YYYY-MM-DD'),
    },
    {
      label: 'Amanhã configurados',
      description: 'Pregões configurados com abertura de lances amanhã',
      status: 'Configurada',
      dateStart: moment().add(1, 'day').format('YYYY-MM-DD'),
      dateEnd: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        fontSize: '14px',
        fontWeight: 'bold',
      },
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
      },
    },
    rows: {
      style: {
        fontSize: '14px',
      },
    },
  };

  return (
    <>
      <div className="container-fluid big-resolutions p-4">
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h4 className="mb-0">Licitações</h4>
              {filteredList && (
                <small className="text-muted">
                  {filteredList.length} resultado{filteredList.length !== 1 ? 's' : ''}
                </small>
              )}
            </div>

            <div className="card card-body mb-3 p-3">
              <div className="d-flex">
                <div className="border-right pr-3 mr-3" style={{ minWidth: '240px' }}>
                  <p className="small text-muted mb-2">Acesso rápido</p>
                  {quickFilters.map((qf) => (
                    <div key={qf.label} className="mb-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm btn-block text-left"
                        onClick={() => applyQuickFilter(qf.status, qf.dateStart, qf.dateEnd)}
                        onMouseEnter={(e) => showTooltip(e, qf.description)}
                        onMouseLeave={hideTooltip}
                      >
                        {qf.label}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex-grow-1">
                  <div className="row">
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-client-id" className="small mb-1">
                        ID
                      </label>
                      <input
                        id="filter-client-id"
                        type="text"
                        className="form-control form-control-sm"
                        value={filterClientId}
                        onChange={(e) => setFilterClientId(e.target.value)}
                        placeholder="Buscar por ID"
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-prg-cod" className="small mb-1">
                        Pregão
                      </label>
                      <input
                        id="filter-prg-cod"
                        type="text"
                        className="form-control form-control-sm"
                        value={filterPrgCod}
                        onChange={(e) => setFilterPrgCod(e.target.value)}
                        placeholder="Buscar por pregão"
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-uasg" className="small mb-1">
                        UASG
                      </label>
                      <input
                        id="filter-uasg"
                        type="text"
                        className="form-control form-control-sm"
                        value={filterUasg}
                        onChange={(e) => setFilterUasg(e.target.value)}
                        placeholder="Buscar por UASG"
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-org-name" className="small mb-1">
                        Nome
                      </label>
                      <input
                        id="filter-org-name"
                        type="text"
                        className="form-control form-control-sm"
                        value={filterOrgName}
                        onChange={(e) => setFilterOrgName(e.target.value)}
                        placeholder="Buscar por nome"
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-portal" className="small mb-1">
                        Portal
                      </label>
                      <select
                        id="filter-portal"
                        className="form-control form-control-sm"
                        value={filterPortal}
                        onChange={(e) => setFilterPortal(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {portalOptions.map((p) => (
                          <option key={p} value={p}>
                            {formatPortalLabel(p)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-status" className="small mb-1">
                        Status
                      </label>
                      <select
                        id="filter-status"
                        className="form-control form-control-sm"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-date-start" className="small mb-1">
                        Abertura Lances — Início
                      </label>
                      <input
                        id="filter-date-start"
                        type="date"
                        className="form-control form-control-sm"
                        value={filterDateStart}
                        onChange={(e) => setFilterDateStart(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-2">
                      <label htmlFor="filter-date-end" className="small mb-1">
                        Abertura Lances — Fim
                      </label>
                      <input
                        id="filter-date-end"
                        type="date"
                        className="form-control form-control-sm"
                        value={filterDateEnd}
                        onChange={(e) => setFilterDateEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-1">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted mr-3"
                      onClick={loadBiddings}
                      disabled={loading}
                    >
                      Recarregar lista
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={clearFilters}
                      disabled={!hasActiveFilters}
                    >
                      Limpar filtros
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="text-danger p-3">Erro ao carregar dados.</div>}

            {loading && (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: 'calc(100vh - 350px)' }}
              >
                <Loader />
                <p className="text-muted mb-2" style={{ fontSize: 14 }}>
                  {downloadProgress >= 0
                    ? `Carregando licitações... ${downloadProgress}%`
                    : 'Carregando licitações...'}
                </p>
                <div style={{ width: 320 }}>
                  <Line
                    percent={downloadProgress >= 0 ? downloadProgress : 100}
                    strokeColor="#007bff"
                    trailColor="#e9ecef"
                    strokeWidth={3}
                  />
                </div>
              </div>
            )}

            {filteredList && (
              <DataTable
                columns={datatableColumns()}
                data={datatableData(filteredList)}
                keyField="id"
                customStyles={customStyles}
                noDataComponent={<div className="p-5">Nenhuma licitação encontrada.</div>}
                pagination
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangeRowsPerPage={(newPerPage) => {
                  setRowsPerPage(newPerPage);
                  localStorage.setItem('robot_biddings_rows_per_page', String(newPerPage));
                }}
                paginationComponentOptions={{
                  rowsPerPageText: 'Itens por página:',
                  rangeSeparatorText: 'de',
                }}
              />
            )}
          </div>
        </div>
      </div>
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.top,
            left: tooltip.left,
            transform: 'translateY(-50%)',
            background: 'rgba(33, 37, 41, 0.9)',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            fontSize: '12px',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              position: 'absolute',
              right: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderRight: '5px solid rgba(33, 37, 41, 0.9)',
            }}
          />
          {tooltip.text}
        </div>
      )}
    </>
  );
};

export default RobotDashboardBiddingsList;
